import React, { useState, useEffect } from "react";
import { HiArrowSmLeft } from "react-icons/hi";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";

const ShiftBookingDrawer = ({
  isOpen,
  onClose,
  onConfirm,
  roomId,
  removedRooms,
  availableRooms,
}) => {
  const [bookingsToShift, setBookingsToShift] = useState([]);
  const [shiftMap, setShiftMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && removedRooms?.length > 0) {
      fetchAffectedBookings();
    }
  }, [isOpen, removedRooms]);

  const fetchAffectedBookings = async () => {
    setLoading(true);

    try {
      const removedRoomNumbers = removedRooms.map((r) => r.room_number);

      const today = new Date();
      const fromDate = new Date(today);
      fromDate.setDate(today.getDate() - 7);
      const toDate = new Date(today);
      toDate.setDate(today.getDate() + 7);

      const payload = {
        dateType: "custom",
        fromDate: fromDate.toISOString().split("T")[0],
        toDate: toDate.toISOString().split("T")[0],
      };

      const response = await httpService.post(
        "/vendorbooking/getVendorBookings",
        payload,
      );

      // Extract bookings from response
      let bookings = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        bookings = response.data.data;
      } else if (Array.isArray(response.data)) {
        bookings = response.data;
      }


      // Filter bookings in removed rooms with active status
      const activeStatuses = ["confirmed", "active", "pending", "started"];
      const affected = bookings.filter((booking) => {
        const bookingRoomNumber = booking.room_number;
        const match =
          booking.room_type_id === roomId &&
          bookingRoomNumber &&
          removedRoomNumbers.includes(bookingRoomNumber) &&
          activeStatuses.includes(booking.status);

        if (match) {
          console.log(
            `Found booking: Room ${bookingRoomNumber} | Pet: ${booking.pet_name} | Status: ${booking.status} | Order: ${booking.order_id}`,
          );
        }
        return match;
      });


      setBookingsToShift(affected);

      // Initialize shift map with order_id as key
      const map = {};
      affected.forEach((booking) => {
        map[booking.order_id] = "";
      });
      setShiftMap(map);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (orderId, targetRoomNumber) => {
    setShiftMap((prev) => ({ ...prev, [orderId]: targetRoomNumber }));
  };

  const hasDuplicateTarget = () => {
    const values = Object.values(shiftMap).filter(Boolean);
    return values.length !== new Set(values).size;
  };

  const allAssigned = () =>
    bookingsToShift.length > 0 &&
    bookingsToShift.every((booking) => shiftMap[booking.order_id]);

  const confirmReady = allAssigned() && !hasDuplicateTarget();

 const handleConfirm = async () => {
   

   if (!confirmReady) {
     toast.error("Please assign all bookings to different rooms");
     return;
   }

   setSaving(true);

   try {
     // Update each booking
     for (const booking of bookingsToShift) {
       const newRoomNumber = shiftMap[booking.order_id];


       // Find target room details
       const targetRoom = availableRooms.find(
         (r) => r.room_number === newRoomNumber,
       );

       if (!targetRoom) {
         console.error(`Room ${newRoomNumber} not found in available rooms`);
         toast.error(`Room ${newRoomNumber} not found`);
         continue;
       }

       // IMPORTANT: Make sure room_number is a string, not empty or null
       const finalRoomNumber = String(newRoomNumber).trim();

       // Prepare the order item with updated room
       const orderItem = {
         item_type: booking.type || "boarding",
         vendor_pet_id: booking.vendor_pet_id,
         service_id: booking.service_id,
         room_type_id: booking.room_type_id,
         room_id: targetRoom._id || targetRoom.id,
         room_number: finalRoomNumber, // Ensure this is a valid string
         start_date: booking.start_date?.split("T")[0],
         end_date: booking.end_date?.split("T")[0],
         duration: booking.duration,
         quantity: booking.quantity,
         addons: booking.addons || [],
         notes: booking.notes || "",
         pet_name: booking.pet_name,
         roomName: targetRoom.room_name || `Room ${finalRoomNumber}`,
         status: booking.status,
         ref_id: booking.ref_id,
       };

       const payload = {
         booking_type: 2,
         order_id: booking.order_id,
         order_type: "offline",
         order_items: [orderItem],
         notes: booking.order_notes || "",
       };

     
       const response = await httpService.post(
         "/vendororder/addVendorOrderOrUpdate",
         payload,
       );
     

       // Verify the response has room_number
       if (response.data?.data?.order_items?.[0]?.room_number) {
         console.log(
           `Verified: room_number is now ${response.data.data.order_items[0].room_number}`,
         );
       } else {
         console.warn(
           `Warning: room_number might not have been updated in the response`,
         );
       }
     }

     // Update room - remove the deleted room numbers
     const roomRes = await httpService.get(`/room/getRoom/${roomId}`);
     const roomDetails = roomRes.data?.data || roomRes.data;

     // Handle room_numbers
     let currentRoomNumbers = roomDetails.room_numbers || [];
     let updatedRoomNumbers;

     if (
       currentRoomNumbers.length > 0 &&
       typeof currentRoomNumbers[0] === "object"
     ) {
       updatedRoomNumbers = currentRoomNumbers.filter(
         (room) =>
           !removedRooms.some(
             (r) => String(r.room_number) === String(room.room_number),
           ),
       );
     } else {
       updatedRoomNumbers = currentRoomNumbers.filter(
         (roomNum) =>
           !removedRooms.some((r) => String(r.room_number) === String(roomNum)),
       );
     }

   

     // Format pet_type
     let petTypeIds = [];
     if (roomDetails.pet_type && Array.isArray(roomDetails.pet_type)) {
       petTypeIds = roomDetails.pet_type.map((pet) => {
         if (typeof pet === "object" && pet.id) return pet.id;
         if (typeof pet === "object" && pet._id) return pet._id;
         return pet;
       });
     }

     const roomPayload = {
       room_name: roomDetails.room_name,
       room_category: roomDetails.room_category,
       description: roomDetails.description || "",
       environment_type: roomDetails.environment_type,
       camera: roomDetails.camera,
       pet_type: petTypeIds,
       kennel_size: roomDetails.kennel_size || "",
       occupancy_limit: roomDetails.occupancy_limit,
       images: roomDetails.images || [],
       set_default_numbering: roomDetails.set_default_numbering || "No",
       room_numbers: updatedRoomNumbers,
       is_default_room: roomDetails.is_default_room || "No",
     };

    
     await httpService.put(`/room/updateRoom/${roomId}`, roomPayload);

     toast.success("Bookings shifted successfully!");

     // Wait a moment before refreshing to allow backend to process
     setTimeout(async () => {
       await fetchAffectedBookings();
     }, 500);

     onConfirm();
     onClose();
   } catch (err) {
     console.error("Error during shift:", err);
     console.error("Error response:", err.response?.data);
     toast.error(err.response?.data?.message || "Failed to shift bookings");
   } finally {
     setSaving(false);
   }
 };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div
        className="absolute inset-0 bg-black bg-opacity-40"
        onClick={onClose}
      />
      <div
        className="relative w-full lg:w-2/5 bg-white h-full flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b shadow-sm">
          <button
            onClick={onClose}
            className="rounded-full hover:bg-gray-100 p-1 transition-colors"
          >
            <HiArrowSmLeft className="w-7 h-7" />
          </button>
          <div>
            <h2 className="text-lg font-semibold">Shift Bookings</h2>
            <p className="text-sm text-gray-500">
              Rooms {removedRooms?.map((r) => r.room_number).join(", ")} will be
              removed
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            ⚠️ The following rooms have active bookings. Please assign each
            booking to a new room.
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading bookings...</p>
            </div>
          ) : bookingsToShift.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No active bookings found in the rooms being removed.
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {bookingsToShift.map((booking) => (
                  <div
                    key={booking.order_id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {booking.pet_name}
                          <span className="text-sm font-normal text-gray-500 ml-2">
                            ({booking.pet_breed})
                          </span>
                        </h3>
                        <p className="text-sm text-gray-600">
                          {booking.client_first_name} {booking.client_last_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          📅 {new Date(booking.start_date).toLocaleDateString()}{" "}
                          - {new Date(booking.end_date).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          🏠 Current Room:{" "}
                          <span className="font-medium">
                            {booking.room_number}
                          </span>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Order: {booking.order_number}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          booking.status === "confirmed"
                            ? "bg-green-100 text-green-800"
                            : booking.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {booking.status}
                      </span>
                    </div>

                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Move to new room:
                      </label>
                      <select
                        value={shiftMap[booking.order_id] || ""}
                        onChange={(e) =>
                          handleSelect(booking.order_id, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a room</option>
                        {availableRooms.map((room) => (
                          <option
                            key={room._id || room.id}
                            value={room.room_number}
                          >
                            Room {room.room_number} - {room.room_name || ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {hasDuplicateTarget() && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  ⚠️ Multiple bookings assigned to the same room. Please assign
                  different rooms.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-white flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!confirmReady || saving || loading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Confirm & Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftBookingDrawer;
